import {
    NODE_WIDTH, STEP_HEIGHT, TASK_STEP_HEIGHT, TRIGGER_HEIGHT, ADD_STEP_WIDTH,
    STUB_WIDTH, STUB_GAP, STUB_Y, TRIGGER_Y, STEP_BASE_Y, STEP_GAP_Y,
    DEFAULT_EDGE_COLOR, LAYOUT_GAP_Y,
} from './constants';
import { buildSourceHandleId, TARGET_HANDLE_ID, DEFAULT_SOURCE_HANDLE_ID } from './handles';
import { resolveBranchLabel } from './labels';
import { isApprovalStep, stepBranches } from '@/utils/automationBuilder';

export function buildGraph({
    path, steps, edges: dbEdges,
    onEditStep, onAddStep, onUpdateTrigger,
    onEnrollPerson, onEnrollRole, enrolling,
    onInsertStep, onDeleteEdge, onStepMenu,
    enrollmentStepStatus, readOnly,
    outOfScopeBranches, triggeringForms,
    triggeringEvents,
    actionLookups,
    taskLookups,
    enrollmentStepDetails,
    enrollee,
    onConfigureTrigger,
    triggerOptions,
}) {
    const nodes = [];
    const structuralEdges = [];

    if (!steps?.length) return { nodes, edges: structuralEdges };

    const triggerStep = steps.find(s => s.stepType === 'TRIGGER');
    const nonTriggerSteps = steps.filter(s => s.stepType !== 'TRIGGER');

    const edgeMap = {};
    const incomingTargets = new Set();
    (dbEdges || []).forEach(e => {
        if (!edgeMap[e.sourceStepID]) edgeMap[e.sourceStepID] = [];
        edgeMap[e.sourceStepID].push(e);
        if (e.targetStepID) incomingTargets.add(e.targetStepID);
    });

    const stepPositions = {};
    nonTriggerSteps.forEach((step) => {
        const pos = step.config?.canvasPosition || { x: 260, y: STEP_BASE_Y + (step.position || 0) * STEP_GAP_Y };
        stepPositions[step.id] = pos;
    });

    const dimSet = new Set();
    if (outOfScopeBranches) {
        const walk = (id, visited = new Set()) => {
            if (visited.has(id)) return;
            visited.add(id);
            dimSet.add(id);
            const outs = edgeMap[id] || [];
            outs.forEach(e => walk(e.targetStepID, visited));
        };
        steps.forEach(step => {
            if (step.stepType !== 'CONDITION' && !isApprovalStep(step)) return;
            const stepEdges = edgeMap[step.id] || [];
            stepEdges.forEach(edge => {
                if (outOfScopeBranches.has(`${step.id}:${edge.branchID || 'default'}`)) {
                    walk(edge.targetStepID);
                }
            });
        });
    }

    // Pass 1: step nodes
    nonTriggerSteps.forEach((step) => {
        const pos = stepPositions[step.id];
        const stepStatus = enrollmentStepStatus?.[step.id];
        const isCompleted = stepStatus === 'COMPLETED' || stepStatus === 'SKIPPED';
        const isCurrent = stepStatus === 'WAITING_MANUAL' || stepStatus === 'IN_PROGRESS' || stepStatus === 'WAITING';
        const isFailed = stepStatus === 'FAILED';
        const highlightVariant = isCompleted ? 'success' : (isCurrent ? 'warning' : (isFailed ? 'error' : null));

        nodes.push({
            id: step.id,
            type: 'step',
            position: pos,
            data: {
                step,
                highlightVariant,
                isInvalid: !incomingTargets.has(step.id),
                dim: dimSet.has(step.id),
                onEdit: onEditStep,
                onMenu: onStepMenu,
                readOnly,
                actionLookups,
                taskLookups,
                enrollmentStepDetails,
                enrollee,
            },
            draggable: !readOnly,
            deletable: false,
        });
    });

    // Pass 2: edges + stubs/labels
    nonTriggerSteps.forEach((step) => {
        const stepEdges = edgeMap[step.id] || [];
        const stepPos = stepPositions[step.id];
        const stepHeight = step.stepType === 'TASK' ? TASK_STEP_HEIGHT : STEP_HEIGHT;

        // Branch-source steps: CONDITION (config.branches + implicit
        // 'default' No-Match branch) and REQUEST_APPROVAL actions (fixed
        // Approved/Denied branches, no default).
        const isApproval = isApprovalStep(step);
        if (step.stepType === 'CONDITION' || isApproval) {
            const branches = stepBranches(step);
            const total = isApproval ? branches.length : branches.length + 1;
            const connectedBranchIDs = new Set(stepEdges.map(e => e.branchID || 'default'));
            const unconnectedBranches = branches.filter(b => !connectedBranchIDs.has(b.id));
            const defaultConnected = stepEdges.some(e => e.branchID === 'default' || (!e.branchID && e.branchLabel === 'No Match'));

            // Branch stubs for unconnected named branches (keep the original order and total)
            const stubTargets = unconnectedBranches.map((b) => ({
                branchID: b.id,
                label: resolveBranchLabel({ sourceStep: step, branchID: b.id, defaultLabel: null }),
                isDefault: false,
                index: branches.findIndex(x => x.id === b.id),
                total,
            }));

            // No-Match stub if not connected and not in named list
            if (!isApproval && !defaultConnected && !branches.some(b => b.id === 'default')) {
                stubTargets.push({
                    branchID: 'default',
                    label: resolveBranchLabel({ sourceStep: step, branchID: 'default', defaultLabel: 'No Match' }),
                    isDefault: true,
                    index: branches.length,
                    total,
                });
            }

            stubTargets.forEach((stub) => {
                const totalWidth = stub.total * STUB_WIDTH + (stub.total - 1) * STUB_GAP;
                const x = NODE_WIDTH / 2 - totalWidth / 2 + stub.index * (STUB_WIDTH + STUB_GAP);
                const y = stepHeight + STUB_Y;
                const stubNodeID = `stub-${step.id}-${stub.branchID}`;

                // Keep the branch's horizontal offset, but place the new step a
                // comfortable vertical gap below (matches Tidy Up layout gap).
                const canvasPosition = { x: stepPos.x + x, y: stepPos.y + stepHeight + LAYOUT_GAP_Y };
                // Position of the source step at graph-build time; the editor
                // recomputes from the live node position at click time.
                const basePosition = stepPos;
                nodes.push({
                    id: stubNodeID,
                    type: 'branchStub',
                    position: { x, y },
                    parentId: step.id,
                    data: {
                        label: stub.label,
                        isDefault: stub.isDefault,
                        sourceStepID: step.id,
                        branchID: stub.branchID,
                        branchLabel: stub.label,
                        onAddStep,
                        canvasPosition,
                        basePosition,
                        readOnly,
                    },
                    draggable: false,
                    deletable: false,
                    selectable: false,
                    zIndex: 1,
                });

                structuralEdges.push({
                    id: `stubedge-${step.id}-${stub.branchID}`,
                    source: step.id,
                    sourceHandle: buildSourceHandleId(stub.branchID),
                    target: stubNodeID,
                    targetHandle: TARGET_HANDLE_ID,
                    type: 'stubEdge',
                    data: { label: stub.label, isDefault: stub.isDefault },
                    style: { stroke: '#94a3b8', strokeWidth: 1.5, strokeDasharray: '6 4' },
                    reconnectable: false,
                    deletable: false,
                    selectable: false,
                    focusable: false,
                    interactionWidth: 0,
                    markerEnd: null,
                });
            });

            // Render path edges with labels on the edge (condition labels removed)
            stepEdges.forEach((edge) => {
                const isDefault = edge.branchID === 'default' || (!edge.branchID && edge.branchLabel === 'No Match');
                const branchID = edge.branchID || (isDefault ? 'default' : null);
                const branchLabel = resolveBranchLabel({
                    sourceStep: step,
                    branchID,
                    defaultLabel: edge.branchLabel || (isDefault ? 'No Match' : null),
                });

                structuralEdges.push({
                    id: edge.id,
                    source: edge.sourceStepID,
                    sourceHandle: buildSourceHandleId(branchID),
                    target: edge.targetStepID,
                    targetHandle: TARGET_HANDLE_ID,
                    type: 'pathEdge',
                    style: { stroke: DEFAULT_EDGE_COLOR, strokeWidth: 2 },
                    data: {
                        pathEdgeID: edge.id,
                        branchID,
                        branchLabel,
                        isBranch: true,
                        isDefaultBranch: isDefault,
                        sourceStepID: edge.sourceStepID,
                        targetStepID: edge.targetStepID,
                        dim: dimSet.has(edge.targetStepID),
                        readOnly,
                        onInsertStep,
                        onDeleteEdge,
                    },
                    zIndex: 0,
                });
            });
        } else {
            // Non-condition step: regular outgoing path edge
            stepEdges.forEach((edge) => {
                structuralEdges.push({
                    id: edge.id,
                    source: edge.sourceStepID,
                    sourceHandle: buildSourceHandleId(edge.branchID),
                    target: edge.targetStepID,
                    targetHandle: TARGET_HANDLE_ID,
                    type: 'pathEdge',
                    style: { stroke: DEFAULT_EDGE_COLOR, strokeWidth: 2 },
                    data: {
                        pathEdgeID: edge.id,
                        branchID: edge.branchID || null,
                        branchLabel: edge.branchLabel || null,
                        isBranch: false,
                        isDefaultBranch: false,
                        sourceStepID: edge.sourceStepID,
                        targetStepID: edge.targetStepID,
                        dim: dimSet.has(edge.targetStepID),
                        readOnly,
                        onInsertStep,
                        onDeleteEdge,
                    },
                    zIndex: 0,
                });
            });

            // Add Step node for leaf steps
            if (stepEdges.length === 0) {
                const addX = (NODE_WIDTH - ADD_STEP_WIDTH) / 2;
                const addY = stepHeight + 18;
                const nodeID = `addstep-${step.id}`;

                nodes.push({
                    id: nodeID,
                    type: 'addStep',
                    position: { x: addX, y: addY },
                    parentId: step.id,
                    data: {
                        onAddStep,
                        sourceStepID: step.id,
                        branchID: null,
                        branchLabel: null,
                        // New step goes directly below (same x) with the same
                        // vertical gap the Tidy Up auto-layout uses.
                        canvasPosition: { x: stepPos.x, y: stepPos.y + stepHeight + LAYOUT_GAP_Y },
                        basePosition: stepPos,
                        readOnly,
                    },
                    draggable: false,
                    deletable: false,
                    selectable: false,
                });

                structuralEdges.push({
                    id: `stubedge-${step.id}`,
                    source: step.id,
                    sourceHandle: DEFAULT_SOURCE_HANDLE_ID,
                    target: nodeID,
                    targetHandle: TARGET_HANDLE_ID,
                    type: 'stubEdge',
                    style: { stroke: '#94a3b8', strokeWidth: 1.5, strokeDasharray: '6 4' },
                    reconnectable: false,
                    deletable: false,
                    selectable: false,
                    focusable: false,
                    interactionWidth: 0,
                    markerEnd: null,
                });
            }
        }
    });

    // Trigger node
    if (triggerStep) {
        const triggerPos = triggerStep.config?.canvasPosition || { x: 260, y: TRIGGER_Y };
        const triggerStatus = enrollmentStepStatus?.[triggerStep.id];
        const triggerIsCompleted = readOnly && triggerStatus === 'COMPLETED';

        nodes.push({
            id: triggerStep.id,
            type: 'trigger',
            position: triggerPos,
            data: {
                pathID: path?.id,
                pathRecurrence: path?.Recurrence?.[0] || null,
                triggerConfig: triggerStep.config || {},
                actionLookups,
                onUpdateTrigger,
                onConfigureTrigger,
                triggerOptions,
                onEnrollPerson,
                onEnrollRole,
                enrolling,
                isCompleted: triggerIsCompleted,
                readOnly,
                triggeringForms,
                triggeringEvents,
            },
            draggable: !readOnly,
            deletable: false,
        });

        const triggerEdges = edgeMap[triggerStep.id] || [];
        triggerEdges.forEach((edge) => {
            structuralEdges.push({
                id: edge.id,
                source: edge.sourceStepID,
                sourceHandle: DEFAULT_SOURCE_HANDLE_ID,
                target: edge.targetStepID,
                targetHandle: TARGET_HANDLE_ID,
                type: 'pathEdge',
                style: { stroke: DEFAULT_EDGE_COLOR, strokeWidth: 2 },
                data: {
                    pathEdgeID: edge.id,
                    branchID: null,
                    branchLabel: null,
                    isBranch: false,
                    isDefaultBranch: false,
                    sourceStepID: edge.sourceStepID,
                    targetStepID: edge.targetStepID,
                    dim: dimSet.has(edge.targetStepID),
                    readOnly,
                    onInsertStep,
                    onDeleteEdge,
                },
                zIndex: 0,
            });
        });

        // Add Step after trigger if no outgoing edge
        if (triggerEdges.length === 0) {
            const addX = (NODE_WIDTH - ADD_STEP_WIDTH) / 2;
            const addY = TRIGGER_HEIGHT + 8;
            const nodeID = `addstep-${triggerStep.id}`;

            nodes.push({
                id: nodeID,
                type: 'addStep',
                position: { x: addX, y: addY },
                parentId: triggerStep.id,
                data: {
                    onAddStep,
                    sourceStepID: triggerStep.id,
                    branchID: null,
                    branchLabel: null,
                    // New step goes directly below the trigger (same x) with the
                    // same vertical gap the Tidy Up auto-layout uses.
                    canvasPosition: { x: triggerPos.x, y: triggerPos.y + TRIGGER_HEIGHT + LAYOUT_GAP_Y },
                    basePosition: triggerPos,
                    readOnly,
                },
                draggable: false,
                deletable: false,
                selectable: false,
            });

            structuralEdges.push({
                id: `stubedge-${triggerStep.id}`,
                source: triggerStep.id,
                sourceHandle: DEFAULT_SOURCE_HANDLE_ID,
                target: nodeID,
                targetHandle: TARGET_HANDLE_ID,
                type: 'stubEdge',
                style: { stroke: '#94a3b8', strokeWidth: 1.5, strokeDasharray: '6 4' },
                reconnectable: false,
                deletable: false,
                selectable: false,
                focusable: false,
                interactionWidth: 0,
                markerEnd: null,
            });
        }
    }

    return { nodes, edges: structuralEdges };
}
